// ui/certificate.ts — the Certificate of Humanity (SPEC §8): a DOM artifact on-page in
// Act V styling, plus a 1200×630 share PNG composited on <canvas>, plus the share chain
// (navigator.share(files) → clipboard PNG → download). The fail card (§4) reuses the same
// canvas + share plumbing so a rejection is just as postable as a certificate.
//
// Canvas cannot read CSS custom properties (learned in Phase 2), so the share card carries
// concrete colors + a concrete serif stack. Fonts are awaited before drawing so the webface
// (Cormorant Garamond) paints rather than a fallback.

import { el } from "@/ui/chrome.ts";
import type { CertificateData } from "@/engine/traits.ts";

// The return domain, printed on the share PNG and the DOM cert. Registration pending as of
// 2026-07-13, but the name is final. This string is the only place the display URL lives.
export const SHARE_URL = "thelastcaptcha.com";

const ISSUER = "ISSUED BY THE LAST CAPTCHA · VERIFICATION AUTHORITY";

// Act V palette, concrete (canvas can't read tokens). Mirrors theme/acts.ts act 5.
const INK = "#F2F0EA";
const VOID = "#050505";
const DIM = "#7C7C77";
const SERIF = '"Cormorant Garamond", Georgia, "Times New Roman", serif';
const MONO = '"IBM Plex Mono", ui-monospace, Menlo, monospace';

/** Rarity → the frame/label color on the share card and the DOM border (§8). */
const RARITY_COLOR: Record<string, string> = {
  gold: "#E8C86A",
  "too-perfect": "#E8A13C",
  charming: "#D96C3D",
  standard: "#6D6D67",
};

/** Stamp ink: believer runs warm, skeptic runs cool. */
const STAMP_COLOR: Record<string, string> = {
  BELIEVER: "#C6553A",
  SKEPTIC: "#5E7A8A",
};

export type ShareOutcome = "shared" | "copied" | "downloaded" | "failed";

export interface CertHandlers {
  onAgain(): void;
}

// ── DOM certificate ─────────────────────────────────────────────────────

/** Render the on-page certificate into `host` (Act V styling flows from body[data-act="5"]). */
export function renderCertificate(
  host: HTMLElement,
  data: CertificateData,
  handlers: CertHandlers,
): void {
  host.replaceChildren();

  const cert = el("div", "cert");
  cert.dataset.rarity = data.rarity.key;
  cert.style.setProperty("--rarity", RARITY_COLOR[data.rarity.key] ?? DIM);

  const eyebrow = el("div", "cert__eyebrow");
  eyebrow.textContent = "CERTIFICATE OF HUMANITY";

  const title = el("div", "cert__title");
  title.textContent = data.title;

  const score = el("div", "cert__score");
  const scoreNum = el("span", "cert__score-num");
  scoreNum.textContent = `${data.humanness}%`;
  const scoreLabel = el("span", "cert__score-label");
  scoreLabel.textContent = "human";
  score.append(scoreNum, scoreLabel);

  const rows = el("div", "cert__rows");
  rows.append(row("detected", data.detected.join(" · ") || "—"));
  rows.append(row("flagged behavior", data.flagged));
  if (data.circleScore !== null) {
    rows.append(row("circularity", `${fmtScore(data.circleScore)}%`));
  }

  cert.append(eyebrow, title, score, rows);

  if (data.exhibitA) {
    const ex = el("div", "cert__exhibit");
    const exLabel = el("div", "cert__exhibit-label");
    exLabel.textContent = "EXHIBIT A";
    const quote = el("div", "cert__quote");
    quote.textContent = `“${data.exhibitA}”`;
    ex.append(exLabel, quote);
    cert.append(ex);
  }

  const stamp = el("div", "cert__stamp");
  stamp.dataset.stamp = data.stamp;
  stamp.style.color = STAMP_COLOR[data.stamp] ?? INK;
  stamp.textContent = data.stamp;
  cert.append(stamp);

  const rarity = el("div", "cert__rarity");
  rarity.textContent = data.rarity.label;
  cert.append(rarity);

  const issued = el("div", "cert__issued");
  issued.textContent = `${ISSUER} · ${SHARE_URL}`;
  cert.append(issued);

  // Actions
  const actions = el("div", "cert__actions");
  const shareBtn = el("button", "btn");
  shareBtn.textContent = "SHARE";
  shareBtn.addEventListener("click", () => {
    void runShare(shareBtn, () => composeCertPng(data), "certificate-of-humanity.png",
      `THE LAST CAPTCHA rates me ${data.humanness}% human: “${data.title}”.`);
  });
  const again = el("button", "btn btn--ghost");
  again.textContent = "VERIFY ANOTHER HUMAN";
  again.addEventListener("click", () => handlers.onAgain());
  actions.append(shareBtn, again);

  host.append(cert, actions);
}

function row(label: string, value: string): HTMLElement {
  const r = el("div", "cert__row");
  const k = el("span", "cert__row-k");
  k.textContent = label;
  const v = el("span", "cert__row-v");
  v.textContent = value;
  r.append(k, v);
  return r;
}

// ── Fail card (§4) — a rejection worth posting ──────────────────────────

export interface FailCardInput {
  reasons: string[];
}

/** Wire a fail-card share button (used by the FLAGGED screen in main.ts). */
export function shareFail(button: HTMLButtonElement, input: FailCardInput): void {
  void runShare(button, () => composeFailPng(input), "flagged-for-review.png",
    "THE LAST CAPTCHA flagged me for review. humanity: inconclusive.");
}

// ── Share chain (§8): navigator.share(files) → clipboard → download ──────

/** Drive one share, updating the button label to reflect the outcome. */
async function runShare(
  button: HTMLButtonElement,
  compose: () => Promise<Blob>,
  filename: string,
  text: string,
): Promise<void> {
  const original = button.textContent;
  button.disabled = true;
  button.textContent = "…";
  try {
    const blob = await compose();
    const outcome = await shareImage(blob, filename, text);
    button.textContent =
      outcome === "shared" ? "SHARED"
      : outcome === "copied" ? "COPIED"
      : outcome === "downloaded" ? "SAVED"
      : original;
  } catch {
    button.textContent = original;
  } finally {
    button.disabled = false;
    // Restore the affordance after a beat so it stays reusable.
    window.setTimeout(() => {
      button.textContent = original;
    }, 2200);
  }
}

async function shareImage(blob: Blob, filename: string, text: string): Promise<ShareOutcome> {
  const file = new File([blob], filename, { type: "image/png" });

  // 1. Native share sheet with the file (best on mobile).
  const nav = navigator as Navigator & {
    canShare?: (data: ShareData & { files?: File[] }) => boolean;
  };
  if (typeof nav.share === "function" && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], text });
      return "shared";
    } catch (err) {
      // User dismissed the sheet — not a failure, and not worth falling through for.
      if (err instanceof DOMException && err.name === "AbortError") return "shared";
    }
  }

  // 2. Clipboard image (desktop, secure context).
  try {
    const CI = (window as unknown as { ClipboardItem?: typeof ClipboardItem }).ClipboardItem;
    if (CI && navigator.clipboard && "write" in navigator.clipboard) {
      await navigator.clipboard.write([new CI({ "image/png": blob })]);
      return "copied";
    }
  } catch {
    // fall through to download
  }

  // 3. Download.
  try {
    const url = URL.createObjectURL(blob);
    const a = el("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 4000);
    return "downloaded";
  } catch {
    return "failed";
  }
}

// ── Canvas compositors (1200×630) ───────────────────────────────────────

const W = 1200;
const H = 630;

async function newCanvas(): Promise<{ ctx: CanvasRenderingContext2D; canvas: HTMLCanvasElement }> {
  await ensureFonts();
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d context");
  return { ctx, canvas };
}

/** Compose the certificate share PNG (§8). */
export async function composeCertPng(data: CertificateData): Promise<Blob> {
  const { ctx, canvas } = await newCanvas();
  const accent = RARITY_COLOR[data.rarity.key] ?? DIM;

  frame(ctx, accent);

  center(ctx, "CERTIFICATE OF HUMANITY", 92, { font: `500 22px ${MONO}`, color: DIM, spacing: 6 });
  center(ctx, data.title, 176, { font: `500 66px ${SERIF}`, color: INK, max: 1000 });

  center(ctx, `${data.humanness}% HUMAN`, 244, { font: `500 34px ${SERIF}`, color: accent, spacing: 3 });
  rule(ctx, 292, accent);

  // Detail rows and the Exhibit A block share one downward cursor so they never overprint.
  let y = 332;
  y = detail(ctx, "detected", data.detected.join("  ·  ") || "—", y);
  y = detail(ctx, "flagged behavior", data.flagged, y);
  if (data.circleScore !== null) {
    y = detail(ctx, "circularity", `${fmtScore(data.circleScore)}%`, y);
  }

  if (data.exhibitA) {
    y += 8;
    center(ctx, "EXHIBIT A", y, { font: `500 16px ${MONO}`, color: DIM, spacing: 4 });
    wrapCenter(ctx, `“${data.exhibitA}”`, y + 34, { font: `italic 28px ${SERIF}`, color: INK, max: 940, lineHeight: 36 });
  }

  stamp(ctx, data.stamp);

  center(ctx, `${ISSUER} · ${SHARE_URL}`, 598, { font: `500 17px ${MONO}`, color: DIM, spacing: 1 });

  return toBlob(canvas);
}

/** Compose the FLAGGED fail card (§4) — brutal, official, and funny. */
export async function composeFailPng(input: FailCardInput): Promise<Blob> {
  const { ctx, canvas } = await newCanvas();
  const red = "#C0392B";

  frame(ctx, red);

  center(ctx, "VERIFICATION FAILED", 168, { font: `700 62px ${MONO}`, color: red, spacing: 2, max: 1040 });
  center(ctx, "SUBJECT FLAGGED FOR REVIEW", 232, { font: `500 24px ${MONO}`, color: INK, spacing: 4 });
  center(ctx, "humanity: inconclusive", 268, { font: `italic 24px ${SERIF}`, color: DIM });

  rule(ctx, 316, red);

  // Strike reasons, like a police report.
  ctx.textAlign = "left";
  setFont(ctx, `400 22px ${MONO}`, INK, 0);
  let y = 372;
  for (const reason of input.reasons.slice(0, 3)) {
    const lines = wrap(ctx, `— ${reason}`, 940);
    for (const line of lines) {
      ctx.fillText(line, 130, y);
      y += 34;
    }
    y += 6;
  }

  center(ctx, `${ISSUER} · ${SHARE_URL}`, 596, { font: `500 17px ${MONO}`, color: DIM, spacing: 1 });

  return toBlob(canvas);
}

// ── canvas primitives ───────────────────────────────────────────────────

interface TextOpts {
  font: string;
  color: string;
  spacing?: number;
  max?: number;
}

function frame(ctx: CanvasRenderingContext2D, accent: string): void {
  ctx.fillStyle = VOID;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = accent;
  ctx.lineWidth = 2;
  ctx.strokeRect(28, 28, W - 56, H - 56);
  ctx.strokeStyle = withAlpha(accent, 0.4);
  ctx.lineWidth = 1;
  ctx.strokeRect(38, 38, W - 76, H - 76);
}

function setFont(ctx: CanvasRenderingContext2D, font: string, color: string, spacing: number): void {
  ctx.font = font;
  ctx.fillStyle = color;
  (ctx as unknown as { letterSpacing?: string }).letterSpacing = spacing ? `${spacing}px` : "0px";
}

function center(ctx: CanvasRenderingContext2D, text: string, y: number, o: TextOpts & { lineHeight?: number }): void {
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  setFont(ctx, o.font, o.color, o.spacing ?? 0);
  let str = text;
  if (o.max) str = ellipsize(ctx, str, o.max);
  ctx.fillText(str, W / 2, y);
}

function wrapCenter(
  ctx: CanvasRenderingContext2D,
  text: string,
  y: number,
  o: TextOpts & { lineHeight: number },
): void {
  ctx.textAlign = "center";
  setFont(ctx, o.font, o.color, o.spacing ?? 0);
  const lines = wrap(ctx, text, o.max ?? 900).slice(0, 2);
  lines.forEach((line, i) => ctx.fillText(line, W / 2, y + i * o.lineHeight));
}

/** A labeled detail line, centered: label over value. Returns the next row's y. */
function detail(ctx: CanvasRenderingContext2D, label: string, value: string, y: number): number {
  ctx.textAlign = "center";
  setFont(ctx, `400 14px ${MONO}`, DIM, 3);
  ctx.fillText(label.toUpperCase(), W / 2, y);
  setFont(ctx, `italic 26px ${SERIF}`, INK, 0);
  ctx.fillText(ellipsize(ctx, value, 980), W / 2, y + 28);
  return y + 54;
}

function rule(ctx: CanvasRenderingContext2D, y: number, accent: string): void {
  ctx.strokeStyle = withAlpha(accent, 0.5);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(W / 2 - 90, y);
  ctx.lineTo(W / 2 + 90, y);
  ctx.stroke();
}

function stamp(ctx: CanvasRenderingContext2D, label: string): void {
  const color = STAMP_COLOR[label] ?? INK;
  ctx.save();
  ctx.translate(W - 210, H - 150);
  ctx.rotate((-11 * Math.PI) / 180);
  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  roundRect(ctx, -120, -42, 240, 84, 8);
  ctx.stroke();
  ctx.strokeStyle = withAlpha(color, 0.5);
  ctx.lineWidth = 1;
  roundRect(ctx, -112, -34, 224, 68, 6);
  ctx.stroke();
  setFont(ctx, `700 40px ${MONO}`, color, 3);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, 0, 2);
  ctx.restore();
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function ellipsize(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let s = text;
  while (s.length > 1 && ctx.measureText(`${s}…`).width > maxWidth) s = s.slice(0, -1);
  return `${s.trimEnd()}…`;
}

function withAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return hex;
  const r = parseInt(m[1]!, 16);
  const g = parseInt(m[2]!, 16);
  const b = parseInt(m[3]!, 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function toBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob null"))), "image/png");
  });
}

async function ensureFonts(): Promise<void> {
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  if (!fonts) return;
  try {
    await Promise.all([
      fonts.load(`500 66px "Cormorant Garamond"`),
      fonts.load(`italic 30px "Cormorant Garamond"`),
      fonts.load(`500 22px "IBM Plex Mono"`),
      fonts.load(`700 40px "IBM Plex Mono"`),
    ]);
    await fonts.ready;
  } catch {
    // Fallback serif/mono will paint — acceptable.
  }
}

function fmtScore(s: number): string {
  return Number.isInteger(s) ? String(s) : s.toFixed(1);
}
